import { Injectable } from '@angular/core';
import { CompanyRegistrationPeriodControllerService } from '@iota/iota-management-portal-v4-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, forkJoin, map, mergeMap, of, take } from 'rxjs';
import { getLocationHeader } from 'src/app/shared/utils';
import {
    AddUpdateRemoveRegistrationPeriodAttributes,
    LoadRegistrationPeriods,
    LoadRegistrationPeriodsThatUseProduct,
    LoadRegistrationPeriodsThatUseProductSuccess,
    RegistrationPeriodActionTypes,
    ReloadRegistrationPeriod,
    UpsertRegistrationPeriod
} from '../actions/registration-period.action';

@Injectable()
export class RegistrationPeriodEffects {
    loadRegistrationPeriodsByCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadRegistrationPeriods),
            mergeMap(({ companyId }) =>
                this.apiService.getAllCompanyRegistrationPeriodByCompanyId(companyId).pipe(
                    take(1),
                    map((registrationPeriods) => ({
                        type: RegistrationPeriodActionTypes.LoadRegistrationPeriodsSuccess,
                        registrationPeriods,
                        companyId
                    })),
                    catchError((error) => of({ type: RegistrationPeriodActionTypes.LoadRegistrationPeriodsFailure, error }))
                )
            )
        )
    );

    reloadRegistrationPeriods$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReloadRegistrationPeriod),
            mergeMap(({ registrationPeriodId }) =>
                this.apiService.getCompanyRegistrationPeriodById(registrationPeriodId).pipe(
                    take(1),
                    map((registrationPeriod) => ({
                        type: RegistrationPeriodActionTypes.ReloadRegistrationPeriodSuccess,
                        registrationPeriod
                    })),
                    catchError((error) => of({ type: RegistrationPeriodActionTypes.ReloadRegistrationPeriodFailure, error }))
                )
            )
        )
    );

    upsertRegistrationPeriod$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertRegistrationPeriod),
            mergeMap(({ registrationPeriod }) => {
                return this.apiService.upsertCompanyRegistrationPeriod(registrationPeriod, 'response').pipe(
                    take(1),
                    map((resp) => ({
                        type: RegistrationPeriodActionTypes.ReloadRegistrationPeriod,
                        registrationPeriodId: registrationPeriod.companyRegistrationPeriodId
                            ? registrationPeriod.companyRegistrationPeriodId
                            : getLocationHeader(resp)
                    })),
                    catchError((error) => of({ type: RegistrationPeriodActionTypes.RegistrationPeriodActionFailure, error: error }))
                );
            })
        )
    );

    addUpdateRemoveRegistrationPeriodAttributes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AddUpdateRemoveRegistrationPeriodAttributes),
            mergeMap(({ attributeResult, registrationPeriodId }) =>
                forkJoin([
                    attributeResult.upsertedAttributes?.length > 0
                        ? this.apiService.upsertCompanyRegistrationPeriodAttributes(
                              registrationPeriodId,
                              attributeResult.upsertedAttributes
                          )
                        : of(null),
                    attributeResult.removedAttributeIds?.length > 0
                        ? this.apiService.removeCompanyRegistrationPeriodAttributes(attributeResult.removedAttributeIds)
                        : of(null)
                ]).pipe(
                    take(1),
                    map(() => ({
                        type: RegistrationPeriodActionTypes.ReloadRegistrationPeriod,
                        registrationPeriodId: registrationPeriodId
                    })),
                    catchError((error) => of({ type: RegistrationPeriodActionTypes.RegistrationPeriodActionFailure, error: error }))
                )
            )
        )
    );

    loadRegistrationPeriodsThatUseProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadRegistrationPeriodsThatUseProduct),
            mergeMap(({ productId }) =>
                this.apiService.getAllCompanyRegistrationPeriodsThatUseProduct(productId).pipe(
                    take(1),
                    map((registrationPeriods) =>
                        LoadRegistrationPeriodsThatUseProductSuccess({
                            registrationPeriods,
                            productId
                        })
                    ),
                    catchError((error) => of({ type: RegistrationPeriodActionTypes.LoadRegistrationPeriodsFailure, error }))
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private store: Store,
        private apiService: CompanyRegistrationPeriodControllerService
    ) {}
}
